import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  Sparkles,
  Calendar,
  Clock,
  ArrowRight,
} from 'lucide-react';
import { posts } from '../data/blogPosts';
import { setMetaTags } from '../utils/seo';

const Blog = () => {
  useEffect(() => {
    setMetaTags({
      title: 'Blog — InterviewWala ATS',
      description:
        'Insights for Indian staffing firms — how to track high-volume hiring, avoid Excel chaos, and scale recruitment without losing data.',
      url: '/blog',
      image: '/demo/02-command-center.jpg',
      type: 'website',
    });
  }, []);

  return (
    <div
      className="min-h-screen bg-slate-950 text-slate-100"
      data-testid="blog-index-page"
    >
      {/* Nav */}
      <nav className="fixed top-0 inset-x-0 z-30 backdrop-blur-md bg-slate-950/60 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-slate-900" />
            </div>
            <span
              className="text-lg font-semibold"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              InterviewWala
            </span>
          </Link>
          <div className="flex items-center gap-2 sm:gap-4">
            <Link
              to="/pricing"
              className="text-sm text-slate-300 hover:text-white transition px-3 py-2"
            >
              Pricing
            </Link>
            <Link
              to="/blog"
              className="text-sm text-white font-medium px-3 py-2"
              aria-current="page"
            >
              Blog
            </Link>
            <Link
              to="/login"
              className="text-sm text-slate-300 hover:text-white transition px-3 py-2"
            >
              Sign in
            </Link>
            <Link to="/signup">
              <Button
                size="sm"
                className="bg-gradient-to-r from-teal-400 to-emerald-500 text-slate-900 hover:opacity-90 font-semibold"
              >
                Start free
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      <section className="pt-32 pb-20 px-6 lg:px-10">
        <div className="max-w-4xl mx-auto">
          <header className="mb-14">
            <p className="text-xs uppercase tracking-wider text-teal-400 font-bold mb-2">
              The InterviewWala blog
            </p>
            <h1
              className="text-4xl sm:text-5xl font-bold leading-tight mb-4"
              style={{ fontFamily: 'Manrope, sans-serif' }}
              data-testid="blog-index-title"
            >
              Notes for staffing firms.
            </h1>
            <p className="text-slate-300 max-w-2xl text-base sm:text-lg leading-relaxed">
              Practical insights on running a high-volume recruitment business in
              India — from people who built one. New posts every two weeks.
            </p>
          </header>

          <ul className="space-y-5">
            {posts.map((p) => (
              <li
                key={p.slug}
                data-testid={`blog-card-${p.slug}`}
              >
                <Link
                  to={`/blog/${p.slug}`}
                  className="block group rounded-2xl border border-slate-800 bg-slate-900/40 hover:border-teal-500/40 hover:bg-slate-900/80 transition overflow-hidden"
                >
                  <div className="grid sm:grid-cols-[200px_1fr] gap-0">
                    {p.cover && (
                      <div className="aspect-[16/10] sm:aspect-auto sm:h-full bg-slate-800 overflow-hidden">
                        <img
                          src={p.cover}
                          alt=""
                          loading="lazy"
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                    )}
                    <div className="p-6">
                      <div className="flex items-center gap-3 text-[11px] text-slate-400 mb-2.5">
                        <span className="inline-flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {new Date(p.date).toLocaleDateString(undefined, {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                          })}
                        </span>
                        <span>·</span>
                        <span className="inline-flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {p.readTime}
                        </span>
                      </div>
                      <h2
                        className="text-xl sm:text-2xl font-bold text-slate-100 group-hover:text-teal-300 transition leading-tight mb-2"
                        style={{ fontFamily: 'Manrope, sans-serif' }}
                      >
                        {p.title}
                      </h2>
                      <p className="text-sm text-slate-400 leading-relaxed mb-3 line-clamp-2">
                        {p.excerpt}
                      </p>
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-teal-400 group-hover:gap-2 transition-all">
                        Read article <ArrowRight className="w-3 h-3" />
                      </span>
                    </div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <footer className="border-t border-slate-800 py-8 px-6 lg:px-10">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-500">
          <span>© 2026 InterviewWala ATS</span>
          <div className="flex gap-5">
            <Link to="/" className="hover:text-slate-300">
              Home
            </Link>
            <Link to="/pricing" className="hover:text-slate-300">
              Pricing
            </Link>
            <Link to="/login" className="hover:text-slate-300">
              Sign in
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Blog;
